/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useRef } from 'react';
import { styled } from '@mui/material';
import { ErrorWrapper } from '../ErrorMessage/ErrorAndNoData.styles';

import WorkerWalk from '../../../../assets/images/worker-walk.webp';

// worker-walk.webp is one full walk cycle (both steps) of the peazy-app loading animation, cropped so his head stays still
// and only his legs move. Each frame is timed by how far he moves in it, so at its native 305px height he covers an even
// 78.6px per second; the page moves him at that pace, which keeps his planted foot in place.
const NATIVE_HEIGHT = 305;
const NATIVE_SPEED = 78.6;

const WalkerTrack = styled('div')(() => ({
  width: '100%',
  overflow: 'hidden',
}));

const Walker = styled('img')(() => ({
  display: 'block',
  height: 'min(20rem, 50vh)',
  willChange: 'transform',
}));

const WalkingLoader = ({ alt = 'Loading' }: { alt?: string }): JSX.Element => {
  const trackRef = useRef<HTMLDivElement>(null);
  const walkerRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    const walker = walkerRef.current;
    if (!track || !walker || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let walk: Animation | undefined;
    // Pixel keyframes through the Web Animations API run on the compositor, so he keeps walking smoothly
    // even while the page is busy loading data.
    const startWalk = () => {
      if (!walker.clientWidth) return; // image not loaded yet
      const distance = track.clientWidth - walker.clientWidth;
      const speed = NATIVE_SPEED * (walker.clientHeight / NATIVE_HEIGHT);
      if (distance <= 0 || speed <= 0) return;
      const progress = walk?.currentTime ?? 0;
      walk?.cancel();
      walk = walker.animate(
        [
          { transform: 'translateX(0) scaleX(1)', offset: 0 },
          { transform: `translateX(${distance}px) scaleX(1)`, offset: 0.5 },
          { transform: `translateX(${distance}px) scaleX(-1)`, offset: 0.5 },
          { transform: 'translateX(0) scaleX(-1)', offset: 1 },
        ],
        { duration: ((2 * distance) / speed) * 1000, iterations: Infinity, easing: 'linear' }
      );
      walk.currentTime = progress;
    };

    const observer = new ResizeObserver(startWalk);
    observer.observe(track);
    observer.observe(walker);
    return () => {
      observer.disconnect();
      walk?.cancel();
    };
  }, []);

  return (
    <WalkerTrack ref={trackRef}>
      <Walker ref={walkerRef} src={WorkerWalk} alt={alt} />
    </WalkerTrack>
  );
};

const MapLoader = (): JSX.Element => {
  return (
    <ErrorWrapper>
      <WalkingLoader />
    </ErrorWrapper>
  );
};

export { MapLoader, WalkingLoader };
